/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';

/**
 * Higher-Order Component that provides storefront-based conditional rendering and prop injection.
 *
 * This HOC integrates with the ExperienceContext to access storefront information and applies
 * conditional logic based on the storefront type. It can conditionally hide components or
 * inject props based on the current storefront configuration.
 *
 * @param {React.Component} BaseComponent - The component to wrap with storefront logic
 * @param {Object} [opts={}] - Configuration options for storefront behavior
 * @param {Object|Object[]} [opts.render] - Configuration for conditional rendering based on storefront
 * @param {string} [opts.render.name] - The storefront type to match against. If the current
 *                                     storefront type does NOT match this value, the component
 *                                     will be hidden (renderRules gets false)
 * @param {Object|Object[]} [opts.prop] - Configuration for injecting props based on storefront
 * @param {string} [opts.prop.name] - The storefront type to match against
 * @param {string} [opts.prop.key] - The prop name to inject when storefront type matches
 * @param {*} [opts.prop.value] - The value to inject for the specified prop key
 *
 * @returns {React.Component} Enhanced component with storefront-based logic
 */

export function withStorefront(
  BaseComponent,
  opts = {}
) {

  function StorefrontRule(props) {

    const {
      renderRules: renderRulesProp = [],
      propRules: propRulesProp = {},
    } = props;

    const { customer } = useContext(ExperienceContext);

    const { thd_storefront: storefront = null } = customer || {};

    const renderRules = [...renderRulesProp];

    const propRules = {
      ...propRulesProp
    };

    if (opts.render && storefront?.storeFrontType) {
      if (Array.isArray(opts.render)) {
        opts.render.forEach((renderRule) => {
          if (storefront.storeFrontType !== renderRule.name) {
            renderRules.push(false);
          }
        });
      } else if (storefront.storeFrontType !== opts.render.name) {
        renderRules.push(false);
      }
    }

    if (opts.prop && storefront?.storeFrontType) {
      if (Array.isArray(opts.prop)) {
        opts.prop.forEach((propRule) => {
          if (storefront.storeFrontType === propRule.name) {
            propRules[propRule.key] = propRule.value;
          }
        });
      } else if (storefront.storeFrontType === opts.prop.name) {
        propRules[opts.prop.key] = opts.prop.value;
      }
    }

    return (
      <BaseComponent {...props} renderRules={renderRules} propRules={propRules} />
    );

  }

  StorefrontRule.displayName = 'StorefrontRule';

  StorefrontRule.dataModel = {};

  return StorefrontRule;
}